// Pure-data product list for the in-app-purchase store. Extracted from
// InAppPurchaseStore.tsx so non-component code (e.g. deviceReceiptService)
// can register products at startup without pulling the React/Redux/PouchDB
// import graph in.
//
// `getR6Products()` is started at module load via the `r6Products` promise
// below so the fetch overlaps with the rest of app boot — call sites that
// `await` it get an effectively-cached result.

import { isPlatform } from "@ionic/react";
import { curry, filter, flow } from "lodash/fp";
import { values } from "lodash";

export type JSONProduct = {
    name: string;
    planFeatures: string;
    period: "month" | "year";
    trial: boolean;
    platform: string;
    active: boolean;
};

// Mirror of cdvPurchase v13's ProductType enum — the library's TypeScript
// types don't export it directly, so we keep a local copy. Shared from
// here (rather than duplicated in deviceReceiptService.ts and
// InAppPurchaseStore.tsx) so a future cdvPurchase rename can't silently
// drift between callers.
export enum ProductType {
    CONSUMABLE = "consumable",
    NON_CONSUMABLE = "non consumable",
    /** @deprecated use PAID_SUBSCRIPTION */
    FREE_SUBSCRIPTION = "free subscription",
    PAID_SUBSCRIPTION = "paid subscription",
    NON_RENEWING_SUBSCRIPTION = "non renewing subscription",
    APPLICATION = "application",
}

export const getR6Products = async (): Promise<JSONProduct[]> => {
    const filterByPlatform = curry((p: string, c: JSONProduct[]) =>
        filter({ platform: p }, c)
    );
    const getPlatform = (): string => (isPlatform("ios") ? "ios" : "android");

    try {
        const productsReq = await fetch(
            process.env["REACT_APP_PRODUCTLIST_URL"] ||
                "https://iap.refsix.com/iap.json"
        );

        const processProducts: (p: JSONProduct[]) => JSONProduct[] = flow(
            filterByPlatform(getPlatform())
        );

        const products = (await productsReq.json()) as {
            [k: string]: JSONProduct;
        };

        return processProducts(values(products));
    } catch (err) {
        // Network / parse failure — callers fall back to alternate behaviour
        // (e.g. deviceReceiptService registers a placeholder product so the
        // receipt-load path still works offline). Warn so a regression that
        // breaks every cold start's product load leaves a Sentry/console
        // breadcrumb instead of silently disabling the Go-Pro screen.
        console.warn("getR6Products failed; falling back to empty list", err);
    }
    return [];
};

// Module-level promise so the fetch starts as soon as anything in the app
// imports this module. Both InAppPurchaseStore and deviceReceiptService
// `await` this — whichever runs later effectively gets a cached result.
export const r6Products = getR6Products();
